import { useEffect, useMemo, useState } from 'react';
import { Upload, Pencil, Trash2, Search, X, Loader2, User, ImageOff, UserCircle, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { avatarModelsService, type AvatarModel } from '@/services/avatar-models.service';
import PrivateAvatarUploadModal from '@/components/avatars/PrivateAvatarUploadModal';
import EditPrivateAvatarModal from '@/components/avatars/EditPrivateAvatarModal';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { AVATAR_LIBRARY_PERMISSION } from '@/constants/constant-permissions';

export default function PrivateAvatarLibrary() {
  // Permission guards
  usePermissionRouteAccess([
    AVATAR_LIBRARY_PERMISSION.AVATAR_LIBRARY_READ,
    AVATAR_LIBRARY_PERMISSION.AVATAR_LIBRARY_WRITE,
  ]);
  const canWrite = useHasPermission([AVATAR_LIBRARY_PERMISSION.AVATAR_LIBRARY_WRITE]);

  const [avatars, setAvatars] = useState<AvatarModel[]>([]);
  const [is_loading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [show_upload_modal, setShowUploadModal] = useState(false);
  const [add_look_target, setAddLookTarget] = useState<{ group_id: string; name: string } | null>(null);
  const [editing_avatar, setEditingAvatar] = useState<AvatarModel | null>(null);
  const [deleting_avatar_id, setDeletingAvatarId] = useState<string | null>(null);
  const [confirm_delete_id, setConfirmDeleteId] = useState<string | null>(null);

  const fetchAvatars = async () => {
    setIsLoading(true);
    try {
      const result = await avatarModelsService.listAvatarModels('heygen', {
        limit: 100,
        source: 'private_upload',
        search: search.trim() || undefined,
      });
      setAvatars(result.avatars || []);
    } catch (err) {
      console.error('Failed to fetch private avatars:', err);
      toast.error('Failed to load private avatars');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAvatars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search refetch
  useEffect(() => {
    const handle = setTimeout(() => fetchAvatars(), 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDelete = async (avatar_id: string) => {
    setDeletingAvatarId(avatar_id);
    const result = await avatarModelsService.deletePrivateAvatar(avatar_id);
    setDeletingAvatarId(null);
    setConfirmDeleteId(null);

    if (!result.success) {
      toast.error(result.error || 'Delete failed');
      return;
    }
    toast.success(
      result.deleted_on_heygen
        ? 'Avatar deleted from HeyGen and removed from your library'
        : 'Avatar removed from your library',
    );
    setAvatars((prev) => prev.filter((a) => a.id !== avatar_id));
  };

  const stats = useMemo(() => {
    const total = avatars.length;
    const male = avatars.filter(a => a.gender === 'male').length;
    const female = avatars.filter(a => a.gender === 'female').length;
    const other = total - male - female;
    return { total, male, female, other };
  }, [avatars]);

  // Looks-per-group count for the "+N looks" badge on each card
  const looks_per_group = useMemo(() => {
    const map = new Map<string, number>();
    avatars.forEach((a) => {
      const gid = (a.metadata?.group_id as string) || a.id;
      map.set(gid, (map.get(gid) || 0) + 1);
    });
    return map;
  }, [avatars]);

  return (
    <div className="space-y-6">
      {/* Hero header — matches Course Catalog styling */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <UserCircle className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Private Avatar Library</h1>
              <p className="text-purple-100">Manage your uploaded Photo Avatars used in talking-head videos.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => canWrite && setShowUploadModal(true)}
            disabled={!canWrite}
            title={!canWrite ? 'You do not have permission to upload avatars' : 'Upload a new Photo Avatar'}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/20"
          >
            <Upload className="w-5 h-5" />
            <span className="font-medium">Upload Avatar</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-purple-200">Total Avatars</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{stats.male} / {stats.female} / {stats.other}</div>
            <div className="text-sm text-purple-200">Male · Female · Other</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">Avatar IV</div>
            <div className="text-sm text-purple-200">Engine · $3/min generation</div>
          </div>
        </div>
      </div>

      <div className="card p-4 sm:p-6">
        {/* Search */}
        <div className="relative max-w-sm mb-6">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search avatars by name..."
            className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Grid */}
      {is_loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 animate-pulse">
              <div className="w-full aspect-square rounded-md bg-gray-200 mb-2" />
              <div className="h-3 w-3/4 bg-gray-200 rounded mb-1" />
              <div className="h-2.5 w-1/2 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : avatars.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <h3 className="text-sm font-medium text-gray-700 mb-1">No private avatars yet</h3>
          <p className="text-xs text-gray-500 mb-4">
            {search
              ? `No avatars match "${search}"`
              : 'Upload a photo to create your first private avatar.'}
          </p>
          {!search && (
            <button
              type="button"
              onClick={() => canWrite && setShowUploadModal(true)}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission to upload avatars' : undefined}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-600"
            >
              <Upload className="w-3.5 h-3.5" />
              Upload your first avatar
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {avatars.map((avatar) => {
            const group_id = (avatar.metadata?.group_id as string) || avatar.id;
            const looks_count = looks_per_group.get(group_id) || 1;
            return (
            <div
              key={avatar.id}
              className="group relative border border-gray-200 rounded-lg p-3 hover:border-purple-300 hover:shadow-sm transition-all bg-white"
            >
              {/* Preview */}
              <div className="w-full aspect-square rounded-md overflow-hidden bg-gray-100 mb-2 relative">
                {avatar.preview_image_url ? (
                  <img
                    src={avatar.preview_image_url}
                    alt={avatar.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <ImageOff className="w-8 h-8" />
                  </div>
                )}
                <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-purple-600 text-white text-[8px] font-semibold rounded uppercase tracking-wide">
                  Private
                </span>
                {looks_count > 1 && (
                  <span
                    className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-black/60 text-white text-[8px] font-semibold rounded"
                    title={`${looks_count} looks in this avatar group`}
                  >
                    {looks_count} looks
                  </span>
                )}
              </div>

              {/* Name + gender */}
              <div className="space-y-0.5 mb-2">
                <div className="text-xs font-medium text-gray-800 truncate" title={avatar.name}>
                  {avatar.name}
                </div>
                <div className="text-[10px] text-gray-400 capitalize">
                  {avatar.gender || 'Unknown gender'}
                </div>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => canWrite && setAddLookTarget({ group_id, name: avatar.name })}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission to manage avatars' : 'Add another look (outfit / setting) to this avatar group'}
                  className="inline-flex items-center justify-center gap-1 px-1.5 py-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-50"
                >
                  <Plus className="w-3 h-3" />
                  Look
                </button>
                <button
                  type="button"
                  onClick={() => canWrite && setEditingAvatar(avatar)}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission to edit avatars' : 'Edit'}
                  className="inline-flex items-center justify-center gap-1 px-1.5 py-1 text-[10px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-50"
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => canWrite && setConfirmDeleteId(avatar.id)}
                  disabled={!canWrite || deleting_avatar_id === avatar.id}
                  title={!canWrite ? 'You do not have permission to delete avatars' : 'Delete'}
                  className="inline-flex items-center justify-center gap-1 px-1.5 py-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-50 transition-colors"
                >
                  {deleting_avatar_id === avatar.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Delete
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
      </div>

      {/* Upload modal — create new avatar */}
      {show_upload_modal && (
        <PrivateAvatarUploadModal
          onClose={() => setShowUploadModal(false)}
          onCreated={() => {
            fetchAvatars();
          }}
        />
      )}

      {/* Upload modal — add new look to existing group */}
      {add_look_target && (
        <PrivateAvatarUploadModal
          attach_to_group_id={add_look_target.group_id}
          attach_to_group_name={add_look_target.name}
          onClose={() => setAddLookTarget(null)}
          onCreated={() => {
            fetchAvatars();
          }}
        />
      )}

      {/* Edit modal */}
      {editing_avatar && (
        <EditPrivateAvatarModal
          avatar={editing_avatar}
          onClose={() => setEditingAvatar(null)}
          onSaved={() => {
            fetchAvatars();
          }}
        />
      )}

      {/* Delete confirmation */}
      {confirm_delete_id && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-sm">
            <div className="px-5 py-4 border-b">
              <h3 className="text-sm font-semibold text-gray-900">Remove avatar?</h3>
            </div>
            <div className="px-5 py-4 text-xs text-gray-600 space-y-2">
              <p>This avatar will be removed from your library AND permanently deleted from HeyGen. This action cannot be undone.</p>
              <p className="text-[10px] text-gray-400">
                Any course that referenced this avatar will keep its already-generated video, but won't be able to regenerate using this avatar in the future.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(confirm_delete_id)}
                disabled={deleting_avatar_id === confirm_delete_id}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              >
                {deleting_avatar_id === confirm_delete_id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
